import React from "react";
import { useState } from "owa-react";
import { Button, Tooltip } from "@fluentui/react-components";
import { ChevronLeft12Filled, ChevronRight12Filled } from "@fluentui/react-icons";
import { observer } from "owa-mobx-react";
import classnames from "owa-classnames";
import { useResizeObserver } from "owa-react-hooks/lib/useResizeObserver";
import { setShowFolderPane, setShowFolderPaneAsTransientOverlay, shouldShowFavoritesSideRail } from "owa-mail-layout";
import { getStore as getSharedFavoritesStore } from "owa-favorites";
import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { isMonarchMultipleAccountsEnabled } from "owa-account-source-list";
import { getAccountKeyForMailboxInfo } from "owa-client-types";
import { getIsBitSet, type ListViewBitFlagsMasks } from "owa-bit-flags/lib/utils/listViewBitFlagsUtil";
import { isCapabilityEnabled } from "owa-capabilities";
import { bleedThroughCapability } from "owa-capabilities-definitions/lib/bleedThroughCapability";
import loc, { isCurrentCultureRightToLeft } from "owa-localize";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import isWin32StylingEnabled from "owa-theme-win32-style-store/lib/selectors/isWin32StylingEnabled";
import folderStore from "owa-folders";
import publicFolderFavoriteStore from "owa-public-folder-favorite/lib/store/publicFolderFavoriteStore";
import type { FavoriteFolderData, FavoritePublicFolderData } from "owa-favorites-types";
import { expandNavigationPaneButton as Strings_expandNavigationPaneButton } from "./FavoritesSideRail.locstring.json";
import FavoritesSideRailNode from "./FavoritesSideRailNode";
import { sideRailContainer, sideRailContainerRtl, sideRailContainerBleedThrough, sideRailContainerBleedThroughRtl, sideRail, sideRailHeader, sideRailToggleButton, sideRailToggleIcon, sideRailItemList, } from "./FavoritesSideRail.scss";

const MAX_FAVORITES = 20;
const ITEM_GAP_PX = 2;
const LIST_HEIGHT_RATIO = 0.9; // Leave 10% height buffer

const FavoritesSideRail: React.FunctionComponent = observer(function FavoritesSideRail() {
    const railRef = React.useRef<HTMLDivElement>(null);
    const headerRef = React.useRef<HTMLDivElement>(null);
    const headerHeightRef = React.useRef(0);
    
    const favoritesStore = getSharedFavoritesStore();
    
    const allOrdered = isMonarchMultipleAccountsEnabled()
        ? favoritesStore.orderedCombinedOutlookFavoritesIds
        : (favoritesStore.favoriteTreeData.get(getAccountKeyForMailboxInfo(getGlobalSettingsAccountMailboxInfo()))?.orderedOutlookFavoritesIds ?? []);
    
    const cappedIds = allOrdered
        .filter(id => {
        const data = favoritesStore.outlookFavorites.get(id);
        if (!data) {
            return false;
        }
        const type = data.type;
        if (type === "folder" ||
            type === "localFolderArchive" ||
            type === "localFolderShared" ||
            type === "localFolderPst") {
            return !!folderStore().folderTable.get((data as FavoriteFolderData).folderId);
        }
        if (type === "publicFolder") {
            return !!publicFolderFavoriteStore().folderTable.get((data as FavoritePublicFolderData).publicFolderId);
        }
        return true;
    })
        .slice(0, MAX_FAVORITES);
    
    const [capacity, setCapacity] = useState(cappedIds.length);
    
    const itemHeightsRef = React.useRef<Map<string, number>>(new Map());
    const cappedIdsRef = React.useRef(cappedIds);
    cappedIdsRef.current = cappedIds;
    const railHeightRef = React.useRef(0);
    
    const calculateItemsToShow = React.useCallback(() => {
        const availableListHeight = Math.max(0, railHeightRef.current - headerHeightRef.current);
        const maxHeight = availableListHeight * LIST_HEIGHT_RATIO;
        if (maxHeight <= 0) {
            return;
        }
        let totalHeight = 0;
        let fittingCount = 0;
        for (const favoriteId of cappedIdsRef.current) {
            const itemHeight = itemHeightsRef.current.get(favoriteId);
            if (itemHeight === undefined) {
                return;
            }
            const slotHeight = itemHeight + (fittingCount > 0 ? ITEM_GAP_PX : 0);
            if (totalHeight + slotHeight > maxHeight) {
                break;
            }
            totalHeight += slotHeight;
            fittingCount += 1;
        }
        setCapacity(fittingCount);
    }, []);
    
    const onItemHeightChange = React.useCallback((favoriteId: string, height: number) => {
        itemHeightsRef.current.set(favoriteId, height);
        calculateItemsToShow();
    }, [calculateItemsToShow]);
    
    // Resets capacity to the full length when the rail grows so previously-trimmed
    // items get a chance to mount and remeasure.
    const previousRailHeightRef = React.useRef(0);
    const onRailResize = React.useCallback((bounds: DOMRectReadOnly) => {
        const previousHeight = previousRailHeightRef.current;
        previousRailHeightRef.current = bounds.height;
        railHeightRef.current = bounds.height;
        if (bounds.height > previousHeight) {
            setCapacity(cappedIdsRef.current.length);
        }
        calculateItemsToShow();
    }, [calculateItemsToShow]);
    useResizeObserver("FavoritesSideRail", railRef, onRailResize);
    
    const previousCappedCountRef = React.useRef(cappedIds.length);
    if (cappedIds.length > previousCappedCountRef.current) {
        setCapacity(cappedIds.length);
    }
    previousCappedCountRef.current = cappedIds.length;
    
    const onHeaderResize = React.useCallback((bounds: DOMRectReadOnly) => {
        headerHeightRef.current = bounds.height;
        calculateItemsToShow();
    }, [calculateItemsToShow]);
    useResizeObserver("FavoritesSideRailHeader", headerRef, onHeaderResize);
    
    const showWin32OverlayUI = isWin32StylingEnabled();
    const shouldHideFavoritesList = getIsBitSet(16384, getGlobalSettingsAccountMailboxInfo());
    
    const onExpandPane = React.useCallback(() => {
        if (showWin32OverlayUI) {
            setShowFolderPaneAsTransientOverlay(true);
            return;
        }
        
        setShowFolderPane(true);
    }, [showWin32OverlayUI]);
    
    if (!shouldShowFavoritesSideRail()) {
        return null;
    }
    
    // Preserve legacy behavior when overlay styling is disabled.
    if (!showWin32OverlayUI && cappedIds.length === 0) {
        return null;
    }
    
    const isBleedThroughEnabled = isCapabilityEnabled(bleedThroughCapability);
    const isRTL = isCurrentCultureRightToLeft();
    const expandPaneLabel = loc(Strings_expandNavigationPaneButton);
    const expandPaneIcon = isRTL ? (<ChevronLeft12Filled className={sideRailToggleIcon}/>) : (<ChevronRight12Filled className={sideRailToggleIcon}/>);
    
    return (<div className={classnames(sideRailContainer, isRTL && sideRailContainerRtl, isBleedThroughEnabled && sideRailContainerBleedThrough, isBleedThroughEnabled && isRTL && sideRailContainerBleedThroughRtl)}>
            <div ref={railRef} className={sideRail}>
                {showWin32OverlayUI && (<div ref={headerRef} className={sideRailHeader}>
                        <Tooltip content={expandPaneLabel} relationship="label" positioning="after">
                            <Button appearance="subtle" icon={expandPaneIcon} aria-label={expandPaneLabel} className={sideRailToggleButton} onClick={onExpandPane}/>
                        </Tooltip>
                    </div>)}
                {!shouldHideFavoritesList && cappedIds.length > 0 && (<div className={sideRailItemList} role="list" aria-label="Favorites">
                        {cappedIds.slice(0, capacity).map(favoriteId => (<FavoritesSideRailNode key={favoriteId} favoriteId={favoriteId} hideIcon={!isGlobalFeatureEnabled("fp-vertical-favorites-icons")} onHeightChange={onItemHeightChange}/>))}
                    </div>)}
            </div>
        </div>);
}, "FavoritesSideRail");

export default FavoritesSideRail;
